// src/pages/public/CityServiceLandingPage.tsx
import { useMemo } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import PublicSeoPage from './PublicSeoPage';
import { useSeoInventory, usePublicDirectory } from './usePublicSeoData';
import { cityRow, resolveCityDisplayName } from './publicPageHelpers';
import { getSeoContext } from '../../seo/seoContext';
import { buildCityServiceModel } from '../../seo/core/pageModels';
import { resolveState } from '../../seo/core/states';
import { resolveServiceCategory } from '../../seo/core/taxonomy';
import { citySlug } from '../../seo/core/slug';
import { analyzeFacetedUrl, applyFacetsToMetadata } from '../../seo/core/facets';
import { ROUTES } from '../../seo/core/urls';
import type { PublicPageModel } from '../../seo/types';

/**
 * `/locations/:stateSlug/:citySlug/:serviceSlug` — the most specific, and
 * therefore the thinnest, public URL shape.
 *
 * It is also where the eligibility engine matters most: this is the level that
 * would otherwise produce tens of thousands of near-empty pages. A combination
 * only becomes indexable once it holds enough real supply, and only if its
 * parent city page is indexable too.
 */
function CityServiceLandingPage() {
  const ctx = getSeoContext();
  const params = useParams<{ stateSlug: string; citySlug: string; serviceSlug: string }>();
  const [searchParams] = useSearchParams();
  const state = resolveState(params.stateSlug);
  const category = resolveServiceCategory(params.serviceSlug);
  const slug = citySlug(params.citySlug);
  const { inventory, loading: inventoryLoading } = useSeoInventory();

  const cityName = state ? resolveCityDisplayName(inventory, state.code, slug) : '';

  const { directory, loading: directoryLoading } = usePublicDirectory(
    { state: state?.code, city: cityName, serviceCategory: category?.slug, limit: 24 },
    Boolean(state && cityName && category)
  );

  const model = useMemo<PublicPageModel | null>(() => {
    if (!state || !category || !cityName) return null;
    const row = cityRow(inventory, state.code, slug);
    const match = (row?.services || []).find((s) => s.serviceCategory === category.slug);

    const built = buildCityServiceModel(
      {
        city: cityName,
        state: state.code,
        serviceCategory: category.slug,
        stylistCount: match?.stylistCount ?? directory.total,
        priceMin: directory.priceMin,
        priceMax: directory.priceMax,
        stylists: directory.stylists,
        siblingServices: row?.services || [],
        siblingCities: (inventory?.locations || [])
          .filter(
            (other) =>
              citySlug(other.city) !== slug &&
              (other.services || []).some((s) => s.serviceCategory === category.slug)
          )
          .slice(0, 12),
      },
      ctx
    ) as PublicPageModel | null;
    if (!built) return null;

    const analysis = analyzeFacetedUrl(
      ROUTES.cityService(state.slug, cityName, category.slug),
      searchParams,
      { baseIndexable: built.metadata.indexable }
    );
    return { ...built, metadata: applyFacetsToMetadata(built.metadata, analysis, ctx) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, category, cityName, slug, inventory, directory, searchParams.toString(), ctx.environment, ctx.siteUrl]);

  if (!state) return <Navigate to={ROUTES.locationIndex()} replace />;
  if (!slug) return <Navigate to={ROUTES.state(state.slug)} replace />;
  // Unknown service under a real city: fall back to the broader city page
  // rather than rendering a URL that can never have inventory.
  if (!category) return <Navigate to={ROUTES.city(state.slug, slug)} replace />;
  if (
    state.slug !== params.stateSlug ||
    slug !== params.citySlug ||
    category.slug !== params.serviceSlug
  ) {
    return <Navigate to={ROUTES.cityService(state.slug, slug, category.slug)} replace />;
  }

  return <PublicSeoPage model={model} loading={inventoryLoading || directoryLoading} />;
}

export default CityServiceLandingPage;
